import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { soundService } from '../services/SoundNotificationService';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Load notifications from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('notifications');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setNotifications(parsed);
        setUnreadCount(parsed.filter(n => !n.read).length);
      } catch (e) {
        console.warn('Failed to parse stored notifications');
      }
    }
  }, []);

  // Save notifications to localStorage when they change
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications.slice(0, 50))); // Keep last 50
    setUnreadCount(notifications.filter(n => !n.read).length);
  }, [notifications]);

  // Handle incoming notification
  const handleNotification = useCallback((notification) => {
    // Add to notifications list
    setNotifications(prev => [notification, ...prev].slice(0, 100));

    // Play sound
    if (notification.sound_type) {
      soundService.play(notification.sound_type);
    }

    // Show toast with action for chat messages
    const toastOptions = {
      duration: 5000,
      description: notification.message,
    };

    // Add action button for chat notifications
    if (notification.type === 'chat_message' && notification.data?.conversation_id) {
      toastOptions.action = {
        label: 'Voir',
        onClick: () => {
          window.location.href = `/chat?conversation_id=${notification.data.conversation_id}`;
        }
      };
    }

    switch (notification.priority) {
      case 'critical':
        toast.error(notification.title, toastOptions);
        break;
      case 'high':
        toast.warning(notification.title, toastOptions);
        break;
      default:
        toast.info(notification.title, toastOptions);
    }
  }, []);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!user?.id || !token) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Build WebSocket URL
    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/notifications/${user.id}?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          if (notification.type) {
            handleNotification(notification);
          }
        } catch (e) {
          console.warn('Failed to parse WebSocket message', e);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed', event.code);
        setIsConnected(false);
        
        // Attempt reconnection
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
        }
      };

      ws.onerror = (error) => {
        console.warn('WebSocket error', error);
      };

      // Ping to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      wsRef.current = ws;

      return () => {
        clearInterval(pingInterval);
        ws.close();
      };
    } catch (error) {
      console.warn('Failed to connect WebSocket', error);
    }
  }, [user?.id, token, handleNotification]);

  // Connect when user logs in
  useEffect(() => {
    if (user?.id && token) {
      connectWebSocket();
      
      // Load unread notifications from API on login
      const loadUnreadNotifications = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/notifications/?user_id=${user.id}&unread_only=true&limit=20`);
          if (response.ok) {
            const unreadNotifs = await response.json();
            if (unreadNotifs && unreadNotifs.length > 0) {
              setNotifications(prev => {
                // Merge with existing, avoiding duplicates
                const existingIds = new Set(prev.map(n => n.id));
                const newNotifs = unreadNotifs.filter(n => !existingIds.has(n.id));
                return [...newNotifs, ...prev].slice(0, 50);
              });
            }
          }
        } catch (error) {
          console.warn('Failed to load unread notifications:', error);
        }
      };
      
      loadUnreadNotifications();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [user?.id, token, connectWebSocket]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    localStorage.removeItem('notifications');
  }, []);

  // Add a test notification (for development)
  const addTestNotification = useCallback(() => {
    const testNotification = {
      id: `test_${Date.now()}`,
      type: 'order_assigned',
      sound_type: 'newOrder',
      title: 'Test Notification',
      message: 'This is a test notification message',
      data: {},
      priority: 'high',
      timestamp: new Date().toISOString(),
      read: false
    };
    handleNotification(testNotification);
  }, [handleNotification]);

  const value = {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    clearAll,
    addTestNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
